import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ThemeContext,
  THEME_STORAGE_KEY,
  type ResolvedTheme,
  type Theme,
} from './theme-context';

const SYSTEM_QUERY = '(prefers-color-scheme: dark)';

function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'industrial' || stored === 'system') {
      return stored;
    }
  } catch {
    /* localStorage unavailable (private mode / SSR) — fall through to default */
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(SYSTEM_QUERY).matches;
}

function resolve(theme: Theme): ResolvedTheme {
  if (theme === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return theme;
}

/** Set exactly one theme class on <html> (light = no class). Must mirror the
 *  pre-paint script in index.html. */
function applyThemeClass(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.remove('dark', 'industrial');
  if (resolved === 'dark') root.classList.add('dark');
  else if (resolved === 'industrial') root.classList.add('industrial');
}

/**
 * App-wide theme provider (86d37f3g8). Vite SPA — a small custom provider rather
 * than next-themes. Sets the active theme class (`dark` / `industrial` / none)
 * on <html> and persists the choice.
 *
 * The FIRST paint's class is set by the inline script in index.html (so there's
 * no flash); this provider re-asserts the class on mount and on every change to
 * keep React state and the DOM in sync.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolve(readStoredTheme()));

  // Apply the resolved theme to <html> whenever the preference changes.
  useEffect(() => {
    const next = resolve(theme);
    setResolvedTheme(next);
    applyThemeClass(next);
  }, [theme]);

  // When following the system, react to OS-level scheme changes live (system
  // only ever resolves to light/dark — never industrial).
  useEffect(() => {
    if (theme !== 'system') return;
    const mql = window.matchMedia(SYSTEM_QUERY);
    const onChange = () => {
      const next: ResolvedTheme = mql.matches ? 'dark' : 'light';
      setResolvedTheme(next);
      applyThemeClass(next);
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* ignore persistence failure — the in-memory choice still applies */
    }
    setThemeState(next);
  }, []);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
